import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faInfoCircle,
  faTimes,
  faEnvelope,
  faCog,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { ResizableBox } from "react-resizable";
import * as XLSX from "xlsx";
import "react-resizable/css/styles.css";

const MissingNetworkAccessFeeAlert = ({ onClose }) => {
  const [corporates, setCorporates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMissingNetworkAccessFee = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/missing-network-access-fee`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setCorporates(response.data.data.corporates);
        setLoading(false);
      } catch (err) {
        setError("Failed to load corporate records. Please try again.");
        setLoading(false);
      }
    };

    fetchMissingNetworkAccessFee();
  }, []);

  const navigate = useNavigate();

  const handleNotifyIT = (e) => {
    e.stopPropagation();
    const subject = encodeURIComponent(
      "Urgent: Missing Billing Configuration Settings Fees Alert"
    );
    const body = encodeURIComponent(
      `The Billing and Collection system has detected corporate records that are not fully set up in the Billing Configuration Settings Fees. This will cause billing generation to fail. Please assist in configuring the Billing Configuration Settings Fees for the following records:\n\n` +
        corporates
          .map(
            (corp) =>
              `Corporate Code: ${corp.corporateCode}, Name: ${corp.corporateName}`
          )
          .join("\n") +
        `\n\nThese settings can be configured at corporate-settings.`
    );
    window.location.href = `mailto:dev@amaphil.com.ph?subject=${subject}&body=${body}`;
  };

  const handleExportXLSX = () => {
    const worksheetData = corporates.map((corp) => ({
      "Corporate Code": corp.corporateCode,
      "Corporate Name": corp.corporateName,
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MissingBillingConfigurations");
    XLSX.writeFile(workbook, "MissingBillingConfigurationsFeesSettingsRecords.xlsx");
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  const handleNavigateToSettings = (e) => {
    e.stopPropagation();
    navigate("/corporate-settings");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ResizableBox
        width={700}
        height={550}
        minConstraints={[400, 400]}
        maxConstraints={[800, 800]}
        className="bg-white rounded-2xl shadow-xl mx-4 overflow-hidden border border-gray-100"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-red-50 p-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-red-600 text-2xl"
              />
              <h3 className="text-xl font-semibold text-red-600">
                Urgent: Missing Billing Configuration Settings Fees Alert
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 flex-1 overflow-y-auto">
            <p className="text-gray-700 mb-4">
              The Billing and Collection system has detected corporate records
              that are not fully set up in the{" "}
              <strong>Billing Configuration Settings Fees</strong>.{" "}
              <strong>Billing generation will not work as intended</strong> until
              these records are properly configured. You can set up these
              configurations yourself at <strong>corporate settings</strong>. If
              you need assistance, please contact the IT team.
            </p>

            {/* Corporate List */}
            <div className="bg-gray-50 p-4 rounded-xl mb-4 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-blue-500 text-lg"
                />
                <h4 className="text-sm font-medium text-gray-800">
                  Affected Corporate Records
                </h4>
              </div>
              {loading ? (
                <p className="text-sm text-gray-700">Loading...</p>
              ) : error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : corporates.length === 0 ? (
                <p className="text-sm text-gray-700">No records found.</p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {corporates.map((corp) => (
                    <li key={corp.id} className="text-sm text-gray-700">
                      <span className="font-medium">Code:</span>{" "}
                      {corp.corporateCode} |{" "}
                      <span className="font-medium">Name:</span>{" "}
                      {corp.corporateName}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleExportXLSX}
                disabled={loading || error || corporates.length === 0}
                className={`flex-1 py-2 px-4 rounded-xl transition-colors flex items-center justify-center ${
                  loading || error || corporates.length === 0
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Export to XLSX
              </button>
              <button
                onClick={handleNavigateToSettings}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faCog} className="mr-2" />
                Corporate Settings
              </button>
              {/* <button
                onClick={handleNotifyIT}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                Notify IT Team
              </button> */}
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-xl hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </ResizableBox>
    </div>
  );
};

export default MissingNetworkAccessFeeAlert;